import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { templatesAPI, favoritesAPI, ConsentTemplate } from '../services/api';
import { 
  Plus, 
  Edit, 
  Trash2, 
  FileText,
  Search,
  Calendar,
  ClipboardList,
  Star
} from 'lucide-react';

const TemplateList: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<ConsentTemplate[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadTemplates();
    loadFavorites();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await templatesAPI.getAll(true);
      setTemplates(data);
    } catch (err: any) {
      setError('Error al cargar las plantillas: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const ids = await favoritesAPI.getIds();
      setFavoriteIds(ids);
    } catch {
      setFavoriteIds([]);
    }
  };

  const toggleFavorite = async (e: React.MouseEvent, templateId: string) => {
    e.stopPropagation();
    try {
      if (favoriteIds.includes(templateId)) {
        await favoritesAPI.remove(templateId);
        setFavoriteIds((prev) => prev.filter((id) => id !== templateId));
      } else {
        await favoritesAPI.add(templateId);
        setFavoriteIds((prev) => [...prev, templateId]);
      }
    } catch {
      // ignore
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar la plantilla "${title}"?`)) {
      return;
    }

    try {
      await templatesAPI.delete(id);
      await loadTemplates(); // Recargar lista
    } catch (err: any) {
      alert('Error al eliminar la plantilla: ' + (err.response?.data?.detail || err.message));
    }
  };

  const filteredTemplates = templates.filter(template =>
    template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.subprocess.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex min-h-[45vh] flex-col items-center justify-center py-16">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-slate-200 border-t-blue-700" />
          <p className="font-medium text-slate-600">Cargando plantillas…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-8">
      <div className="max-w-7xl mx-auto">
        <div className="page-hero mb-2 flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-blue-700">
              Biblioteca
            </p>
            <h1 className="mb-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Plantillas de consentimientos
            </h1>
            <p className="max-w-2xl text-slate-600">
              Cree, edite y organice plantillas alineadas con los protocolos del hospital.
            </p>
          </div>
          <div className="flex flex-shrink-0 flex-wrap gap-3">
            <button
              type="button"
              onClick={() => navigate('/consents')}
              className="btn-secondary"
            >
              <ClipboardList className="mr-2 h-5 w-5" />
              Llenar consentimientos
            </button>
            <button
              type="button"
              onClick={() => navigate('/templates/create')}
              className="btn-primary"
            >
              <Plus className="mr-2 h-5 w-5" />
              Nueva plantilla
            </button>
          </div>
        </div>

        {/* Barra de búsqueda */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
              placeholder="Buscar por título, código o subproceso..."
            />
          </div>
        </div>

        {/* Mensaje de error */}
        {error && (
          <div className="card bg-red-50 border-red-200 mb-6">
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        )}

        {/* Lista de plantillas */}
        {filteredTemplates.length === 0 ? (
          <div className="card text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {searchTerm ? 'No se encontraron plantillas' : 'No hay plantillas'}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchTerm 
                ? 'Intenta con otros términos de búsqueda' 
                : 'Crea tu primera plantilla de consentimiento informado'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => navigate('/templates/create')}
                className="btn-primary"
              >
                <Plus className="w-5 h-5 mr-2" />
                Crear Primera Plantilla
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => {
              const isFavorite = template.id && favoriteIds.includes(template.id);
              return (
              <div key={template.id} className="card hover:shadow-xl transition-shadow duration-200">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2">
                      {template.title}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                      <FileText className="w-4 h-4" />
                      <span>{template.code}</span>
                      <span>•</span>
                      <span>{template.subprocess}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => template.id && toggleFavorite(e, template.id)}
                    className={`p-1.5 rounded-lg flex-shrink-0 ${isFavorite ? 'text-amber-500 hover:bg-amber-50' : 'text-gray-400 hover:bg-gray-100 hover:text-amber-500'}`}
                    title={isFavorite ? 'Quitar de favoritos' : 'Añadir a favoritos'}
                  >
                    <Star className={`w-5 h-5 ${isFavorite ? 'fill-amber-500' : ''}`} />
                  </button>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>Revisión: {template.revision_date || 'No especificada'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>Versión: {template.version}</span>
                  </div>
                  {template.created_by && (
                    <div className="text-xs text-gray-500">
                      Creado por: {template.created_by}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => navigate(`/templates/edit/${template.id}`)}
                    className="flex-1 btn-secondary text-sm py-2"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(template.id!, template.title)}
                    className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors duration-200 border border-red-200"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );})}
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateList;
